/**
 * OrderCard Component
 *
 * Displays order information in gallery grid with actions.
 * Shows thumbnail, service type, date, status, and action buttons.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - Tailwind CSS core utilities only
 * - Greens/blues color palette
 * - Smooth transitions (200-300ms)
 * - Lazy loading for images
 */

import React from 'react';
import { OrderDocument, OrderStatus } from '@/shared/types';
import { Card, Button } from '@/components/ui';
import { getServiceName, getServiceIcon, formatDate } from '@/utils/formatting';

export interface OrderCardProps {
  order: OrderDocument;
  onView: (order: OrderDocument) => void;
  onDownload?: (order: OrderDocument) => void;
  onRetry?: (order: OrderDocument) => void;
  onDelete?: (order: OrderDocument) => void;
}

/**
 * OrderCard Component
 *
 * @param order - Order document data
 * @param onView - Callback to view order in lightbox
 * @param onDownload - Optional download callback
 * @param onRetry - Optional retry callback
 * @param onDelete - Optional delete callback
 */
export function OrderCard({
  order,
  onView,
  onDownload,
  onRetry,
  onDelete,
}: OrderCardProps): React.JSX.Element {
  const serviceName = getServiceName(order.serviceType);
  const serviceIcon = getServiceIcon(order.serviceType);
  const canRetry = order.attempts.length < order.maxAttempts && order.status !== OrderStatus.PROCESSING;

  // Get status color
  const getStatusColor = (status: OrderStatus): string => {
    switch (status) {
      case OrderStatus.COMPLETED:
        return 'text-green-600 bg-green-50 border-green-200';
      case OrderStatus.PROCESSING:
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case OrderStatus.FAILED:
        return 'text-red-600 bg-red-50 border-red-200';
      case OrderStatus.PENDING:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Get thumbnail URL (current result or input image)
  const thumbnailUrl = order.currentResultUrl || order.inputImageUrl || null;

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300">
      {/* Thumbnail */}
      {thumbnailUrl ? (
        <div
          className="relative w-full h-48 bg-gray-100 cursor-pointer"
          onClick={() => onView(order)}
        >
          <img
            src={thumbnailUrl}
            alt={`${serviceName} result`}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          {/* Status Badge Overlay */}
          <div className="absolute top-2 right-2">
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                order.status
              )}`}
            >
              {order.status}
            </span>
          </div>
        </div>
      ) : (
        <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <span className="text-6xl" role="img" aria-label={serviceName}>
            {serviceIcon}
          </span>
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Service Info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-2xl" role="img" aria-label={serviceName}>
              {serviceIcon}
            </span>
            <h3 className="font-semibold text-gray-900">{serviceName}</h3>
          </div>
          <span className="text-sm text-gray-500">{order.creditsCost} credits</span>
        </div>

        {/* Date */}
        <p className="text-sm text-gray-600">
          Created: {formatDate(order.createdAt)}
        </p>

        {/* Attempts Info */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            Attempts: {order.attempts.length} / {order.maxAttempts}
          </span>
          {canRetry && (
            <span className="px-2 py-1 bg-green-100 border border-green-500 text-green-700 rounded-full text-xs font-semibold">
              Retries Available
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => onView(order)}
            className="flex-1"
          >
            View
          </Button>

          {order.status === OrderStatus.COMPLETED && onDownload && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onDownload(order)}
            >
              Download
            </Button>
          )}

          {canRetry && onRetry && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onRetry(order)}
              className="bg-green-50 border-green-500 text-green-700 hover:bg-green-100"
            >
              Retry
            </Button>
          )}
        </div>

        {/* Delete Button (Secondary Row) */}
        {onDelete && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => onDelete(order)}
            className="w-full"
          >
            Delete
          </Button>
        )}
      </div>
    </Card>
  );
}
